import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useStore } from '../store';
import type { Page } from '../types';
import { formatBytes, formatMs } from '../utils/format';

export function PerformanceView() {
  const data = useStore((s) => s.data);
  const ttfbBuckets = useMemo(
    () =>
      data
        ? makeHistogram(
            data.pages.map((p) => p.performance?.ttfb).filter((v): v is number => typeof v === 'number'),
            [0, 100, 300, 500, 1000, 2000, 3000, 5000],
            (v) => `${formatMs(v)}`,
          )
        : [],
    [data],
  );
  const sizeBuckets = useMemo(
    () =>
      data
        ? makeHistogram(
            data.pages.map((p) => p.performance?.htmlBytes).filter((v): v is number => typeof v === 'number'),
            [0, 10_000, 50_000, 100_000, 200_000, 500_000, 1_000_000],
            (v) => formatBytes(v),
          )
        : [],
    [data],
  );

  if (!data) return <></>;

  const slowPages = [...data.pages]
    .filter((p) => p.performance)
    .sort((a, b) => (b.performance!.ttfb ?? 0) - (a.performance!.ttfb ?? 0))
    .slice(0, 10);
  const heavyPages = [...data.pages]
    .filter((p) => p.performance)
    .sort((a, b) => (b.performance!.htmlBytes ?? 0) - (a.performance!.htmlBytes ?? 0))
    .slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard label="TTFB 平均" value={formatMs(data.site.performance.ttfb.avg)} />
        <SummaryCard label="TTFB 中央値" value={formatMs(data.site.performance.ttfb.median)} />
        <SummaryCard label="TTFB p95" value={formatMs(data.site.performance.ttfb.p95)} />
        <SummaryCard label="HTML 平均" value={formatBytes(data.site.performance.htmlSize.avg)} />
        <SummaryCard label="HTML 中央値" value={formatBytes(data.site.performance.htmlSize.median)} />
        <SummaryCard label="HTML p95" value={formatBytes(data.site.performance.htmlSize.p95)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ChartCard title="TTFB 分布" buckets={ttfbBuckets} />
        <ChartCard title="HTML サイズ分布" buckets={sizeBuckets} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PageList title="遅いページ Top 10（TTFB）" pages={slowPages} field="ttfb" />
        <PageList title="重いページ Top 10（HTML）" pages={heavyPages} field="size" />
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function ChartCard({
  title,
  buckets,
}: {
  title: string;
  buckets: { label: string; count: number }[];
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">{title}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={buckets}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" fill="#3b82f6" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PageList({
  title,
  pages,
  field,
}: {
  title: string;
  pages: Page[];
  field: 'ttfb' | 'size';
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">{title}</h3>
      <ul className="space-y-1 text-sm">
        {pages.map((p) => (
          <li key={p.url} className="flex items-baseline justify-between gap-3">
            <a
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="truncate font-mono text-xs text-slate-700 hover:text-slate-900 hover:underline"
              title={p.url}
            >
              {p.url}
            </a>
            <span className="shrink-0 tabular-nums text-slate-500">
              {p.performance
                ? field === 'ttfb'
                  ? formatMs(p.performance.ttfb)
                  : formatBytes(p.performance.htmlBytes)
                : '-'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function makeHistogram(
  values: number[],
  edges: number[],
  format: (v: number) => string,
): { label: string; count: number }[] {
  const buckets: { label: string; count: number }[] = [];
  for (let i = 0; i < edges.length - 1; i++) {
    const lo = edges[i]!;
    const hi = edges[i + 1]!;
    const count = values.filter((v) => v >= lo && v < hi).length;
    // ラベルは「下限–上限」形式。上限不明の表記を避けて読み手が範囲を判断しやすくする
    buckets.push({ label: `${format(lo)}–${format(hi)}`, count });
  }
  // 上限超え
  const last = edges[edges.length - 1]!;
  const overflow = values.filter((v) => v >= last).length;
  buckets.push({ label: `${format(last)}+`, count: overflow });
  return buckets;
}
